import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Socket } from 'socket.io-client';
import { PlayerRegistrationResponse } from '../../../../shared/types/game';

import styles from './AuthPage.module.css';

interface AuthPageProps {
  socket: Socket | null;
  onAuthSuccess: () => void;
  isSocketConnected: boolean;
  isPlayerConnectedToServer: boolean;
}

const AuthPage: React.FC<AuthPageProps> = ({ socket, onAuthSuccess, isSocketConnected, isPlayerConnectedToServer }) => {
  const [isLogin, setIsLogin] = useState(true);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [playerName, setPlayerName] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    setMessage('');
    setPlayerName('');
    setPassword('');
    setConfirmPassword('');
  }, [isLogin]);

  const handleAuthResponse = (response: PlayerRegistrationResponse) => {
    setLoading(false);
    if (response.success && response.token && response.player) {
      localStorage.setItem('shogiAuthToken', response.token);
      localStorage.setItem('shogiPlayerId', response.player.id);
      localStorage.setItem('shogiPlayerName', response.player.name);
      setMessage(response.message || '認証成功！');
      console.log('AuthPage: 認証成功、トークンとプレイヤー情報を保存しました。');
      onAuthSuccess();
    } else {
      setMessage(response.message || '認証に失敗しました。');
      console.error('AuthPage: 認証失敗:', response.message);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    if (!socket || !isSocketConnected || !isPlayerConnectedToServer) {
      setMessage('サーバーに接続していません。しばらく待ってから再度お試しください。');
      setLoading(false);
      console.log('AuthPage: ソケット未接続またはプレイヤー未接続。', { socket, isSocketConnected, isPlayerConnectedToServer });
      return;
    }

    if (!playerName.trim() || !password.trim()) {
      setMessage('プレイヤー名とパスワードは必須です。');
      setLoading(false);
      return;
    }

    if (isLogin) {
      console.log('AuthPage: ログインリクエストを送信します。');
      socket.emit('auth:login', { name: playerName, password }, handleAuthResponse);
    } else {
      if (password !== confirmPassword) {
        setMessage('パスワードが一致しません。');
        setLoading(false);
        return;
      }
      const currentUnregisteredPlayerId = localStorage.getItem('shogiPlayerId');
      console.log('AuthPage: 新規登録リクエストを送信します。');
      socket.emit('auth:register', { currentUnregisteredPlayerId, name: playerName, password }, handleAuthResponse);
    }
  };

  const handleGoBack = () => {
    navigate('/'); // ルートページへ遷移
  };

  return (
    <div className={styles.authContainer}>
      <button onClick={handleGoBack} className={styles.backButton}>
        <span role="img" aria-label="back">↩️</span> 戻る
      </button>

      <h2 className={styles.authTitle}>{isLogin ? 'ログイン' : '新規登録'}</h2>

      {/* ログイン・新規登録モード切り替え（トグルスイッチUI） */}
      <div className={styles.modeToggleContainer}>
        <button
          type="button"
          onClick={() => setIsLogin(true)}
          className={`${styles.modeButton} ${isLogin ? styles.active : ''}`}
          disabled={loading}
        >
          ログイン
        </button>
        <button
          type="button"
          onClick={() => setIsLogin(false)}
          className={`${styles.modeButton} ${!isLogin ? styles.active : ''}`}
          disabled={loading}
        >
          新規登録
        </button>
      </div>

      <form onSubmit={handleSubmit} className={styles.authForm}>
        {/* メッセージ表示エリア */}
        {message && (
          <p className={`${styles.message} ${message.includes('失敗') ? styles.errorMessage : ''}`}>
            {message}
          </p>
        )}

        <div className={styles.formGroup}>
          <label htmlFor="playerName" className={styles.label}>プレイヤー名:</label>
          <input
            type="text"
            id="playerName"
            className={styles.input}
            value={playerName}
            onChange={(e) => setPlayerName(e.target.value)}
            required
            autoComplete={isLogin ? "username" : "new-username"}
            disabled={loading}
          />
        </div>
        <div className={styles.formGroup}>
          <label htmlFor="password" className={styles.label}>パスワード:</label>
          <input
            type="password"
            id="password"
            className={styles.input}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            autoComplete={isLogin ? "current-password" : "new-password"}
            disabled={loading}
          />
        </div>
        {!isLogin && (
          <div className={styles.formGroup}>
            <label htmlFor="confirmPassword" className={styles.label}>パスワード確認:</label>
            <input
              type="password"
              id="confirmPassword"
              className={styles.input}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              autoComplete="new-password"
              disabled={loading}
            />
          </div>
        )}
        <button type="submit" className={styles.submitButton} disabled={loading}>
          {loading ? '処理中...' : (isLogin ? 'ログイン' : '登録')}
        </button>
      </form>
    </div>
  );
};

export default AuthPage;